import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Heart, Ruler, Palette, Star, Sparkles, Award, Gem, BookOpen, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ProductCard from "@/components/products/ProductCard";
import BrandLogo from "@/components/brands/BrandLogo";
import Newsletter from "@/components/common/Newsletter";
import HeroSection from "@/components/common/HeroSection";
import ContentCard from "@/components/common/ContentCard";
import { ClientMarquee } from "@/components/common/ClientMarquee";
import { API_BASE_URL } from "@/lib/constants";
import type { Product } from "@shared/schema";
import { useBrands } from "@/hooks/use-brands";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useStyleHubPosts } from "@/lib/blog";
import JsonLd from "@/components/common/JsonLd";

export default function Home() {
  useDocumentTitle(
    undefined,
    "Hand-picked pieces from brands you love, curated by personal stylist Maria Pastor. Filter fashion by body type and colour palette."
  );
  const { data: featuredProducts = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["featured-products"],
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE_URL}/api/products?featured=1&limit=4`
      );
      if (!res.ok) {
        throw new Error("Failed to fetch featured products");
      }
      const data = await res.json();
      const items = Array.isArray(data) ? data : data.items || [];
      return items.map((item: any) => ({
        ...item,
        price:
          typeof item.price === "string" ? parseFloat(item.price) : item.price,
        officialUrl: item.officialUrl || item.url,
      }));
    },
  });

  const { data: brands = [], isLoading: brandsLoading } = useBrands();

  const { data: styleHub, isLoading: styleHubLoading } = useStyleHubPosts();
  const styleHubItems = styleHub?.posts ?? [];
  // `useStyleHubPosts` sorts newest-first itself rather than trusting the API's
  // default sort order, so the first three entries are the latest guides.
  const featuredGuides = styleHubItems.slice(0, 3);

  return (
    <div className="min-h-screen">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Capsule Chic Hub",
        url: "https://capsulechichub.com",
        logo: "https://capsulechichub.com/icon-512.png",
        description: "Curated fashion picks filtered by body type and colour palette, hand-picked by professional stylist Maria Pastor.",
        founder: {
          "@type": "Person",
          name: "Maria Pastor",
          jobTitle: "Personal Stylist",
        },
        areaServed: {
          "@type": "Country",
          name: "United Kingdom",
        },
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Customer Service",
          email: "hi@mariapersonalshopper.com",
          availableLanguage: ["en-GB"],
          areaServed: "GB",
        },
        sameAs: [
          "https://www.instagram.com/capsulechichub",
          "https://mariapersonalshopper.com",
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Capsule Chic Hub",
        url: "https://capsulechichub.com",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: "https://capsulechichub.com/shop?search={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },
      }} />

      {/* Hero Section */}
      <HeroSection />

      {/* How It Works */}
      <section className="py-16 bg-warm-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="inline-flex items-center px-4 py-2 bg-soft-blush/10 text-soft-blush mb-4">
              Why Shop Here
            </Badge>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-4">
              Shopping that actually works for you
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Finding clothes that flatter you isn't about shopping more. It's about shopping smarter.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-cream/40 dark:bg-gray-800/40">
              <div className="w-14 h-14 bg-soft-blush/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <Ruler className="w-7 h-7 text-soft-blush" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-charcoal dark:text-white mb-3">
                Filter by body shape
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Every piece in the shop is tagged by body type. Find cuts and silhouettes that are actually made to flatter your shape. No more guessing.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-cream/40 dark:bg-gray-800/40">
              <div className="w-14 h-14 bg-soft-blush/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <Palette className="w-7 h-7 text-soft-blush" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-charcoal dark:text-white mb-3">
                Filter by colour palette
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Stop buying colours that wash you out. Shop by your seasonal colour palette: Cool, Warm, Clear, or Deep. Every piece will work with your natural colouring.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-cream/40 dark:bg-gray-800/40">
              <div className="w-14 h-14 bg-soft-blush/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <Star className="w-7 h-7 text-soft-blush" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-charcoal dark:text-white mb-3">
                Maria's personal picks
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Look for the <strong>Maria's Picks</strong> label. Those are the pieces I've hand-selected myself and can create the perfect timeless capsule wardrobe. Flattering, versatile, great quality, and the kind of thing you'll reach for on repeat.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button asChild size="lg">
              <Link href="/shop">
                Start Browsing
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12 bg-warm-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="inline-flex items-center px-4 py-2 bg-soft-blush/10 text-soft-blush mb-4">
              <Heart className="w-4 h-4 mr-2" />
              This Week's Favorites
            </Badge>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-4">
              Maria's Current Picks
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Carefully selected pieces that caught my eye these past few days. Each one has that special something.
            </p>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : featuredProducts && featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {featuredProducts
                .filter(p => p.price != null && !isNaN(p.price) && p.price > 0)
                .slice(0, 4)
                .map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))
              }
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No featured products available.</p>
            </div>
          )}

          <div className="text-center mt-12">
            <Button asChild size="lg">
              <Link href="/shop">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Brand Showcase */}
      <section className="py-20 bg-cream/30 dark:bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-4">
              Curated Brand Partners
            </h2>
          </div>

          {brandsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 mb-12">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-xl h-32 animate-pulse" />
              ))}
            </div>
          ) : brands && brands.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 mb-12">
              {brands.slice(0, 8).map((brand) => (
                <BrandLogo key={brand.id} brand={brand} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No brands available.</p>
            </div>
          )}

          <div className="text-center">
            <Button asChild size="lg">
              <Link href="/shop">
                View All Brands
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Styled for real women */}
      <section className="py-14 md:py-16 bg-warm-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-3">
              Styled for real women
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              An expert stylist who works with real people: every body, every season, every budget.
            </p>
          </div>
          <ClientMarquee />
          <div className="text-center mt-10 px-4 sm:px-6 lg:px-8">
            <Button asChild size="lg">
              <Link href="/shop">
                Shop the edit
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* As heard on BBC Radio Oxford */}
      <section className="py-16 md:py-20 bg-cream/30 dark:bg-gray-800/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-soft-blush font-semibold mb-4">
            As heard on
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-4">
            BBC Radio Oxford's Ask the Experts
          </h2>
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-10">
            Maria joined the show as a personal stylist expert, answering listeners' real questions about body shape, colour and building a wardrobe that actually works.
          </p>
          <figure className="mx-auto max-w-3xl">
            <img
              src="https://cdn.capsulechichub.com/featured-bbc.avif"
              alt="Maria featured on BBC Radio Oxford's Ask the Experts"
              className="w-full rounded-xl shadow-lg bg-white"
              loading="lazy"
              width={1566}
              height={396}
            />
          </figure>
          <div className="mt-10">
            <Button
              asChild
              variant="outline"
              className="border-soft-blush text-soft-blush hover:bg-soft-blush hover:text-white"
            >
              <Link href="/about">
                More about Maria
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-warm-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative overflow-hidden max-w-sm mx-auto">
              <picture>
                <source
                  type="image/avif"
                  srcSet="https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-640.avif 640w, https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-960.avif 960w, https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-1280.avif 1280w"
                  sizes="(min-width: 1024px) 384px, 100vw"
                />
                <source
                  type="image/webp"
                  srcSet="https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-640.webp 640w, https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-960.webp 960w, https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-1280.webp 1280w"
                  sizes="(min-width: 1024px) 384px, 100vw"
                />
                <img
                  src="https://cdn.capsulechichub.com/About-Maria-Personal-Shopper-960.webp"
                  alt="Maria, professional fashion stylist and curator"
                  width={960}
                  height={720}
                  loading="lazy"
                  decoding="async"
                  className="rounded-2xl shadow-2xl w-full h-auto"
                />
              </picture>
              <div className="absolute bottom-2 right-2 md:-bottom-6 md:-right-6 bg-soft-blush text-white p-4 md:p-6 rounded-xl shadow-lg">
                <div className="text-2xl md:text-3xl font-serif font-bold">10+</div>
                <div className="text-xs md:text-sm font-medium">Years Experience</div>
              </div>
            </div>

            <div>
              <Badge className="inline-flex items-center px-4 py-2 bg-soft-blush/10 text-soft-blush mb-6">
                Meet Your Stylist
              </Badge>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-charcoal dark:text-white mb-6">
                Hi, I'm Maria
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                I'm a personal stylist with 10+ years of experience, and honestly? I love every single minute of it. I started Capsule Chic Hub because I kept seeing brilliant clients spend hours scrolling, only to end up with something that didn't quite work. You deserve better than that.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <Sparkles className="text-soft-blush mr-3 h-5 w-5 flex-shrink-0" />
                  <span className="text-base text-gray-600 dark:text-gray-300">1,000+ wardrobes transformed, and counting!</span>
                </div>
                <div className="flex items-center">
                  <Award className="text-soft-blush mr-3 h-5 w-5 flex-shrink-0" />
                  <span className="text-base text-gray-600 dark:text-gray-300">Featured on BBC Radio Oxford's Ask the Experts</span>
                </div>
                <div className="flex items-center">
                  <Gem className="text-soft-blush mr-3 h-5 w-5 flex-shrink-0" />
                  <span className="text-base text-gray-600 dark:text-gray-300">On a mission to make getting dressed the best part of your day</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild>
                  <Link href="/about">
                    More About Me
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-soft-blush text-soft-blush hover:bg-soft-blush hover:text-white">
                  <Link href="/how-i-choose">
                    How I Choose
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Style Hub teasers — internal linking to guides */}
      <section className="py-20 bg-warm-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="inline-flex items-center px-4 py-2 bg-soft-blush/10 text-soft-blush mb-4">
              <BookOpen className="w-4 h-4 mr-2" />
              Style Hub
            </Badge>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-4">
              Styling guides, straight from Maria
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Learn how to dress your shape, find your colours, and build a capsule wardrobe that works for your life.
            </p>
          </div>

          {styleHubLoading ? (
            <div className="space-y-6 mb-10">
              <div className="h-80 md:h-96 bg-muted animate-pulse rounded-2xl" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="h-80 bg-muted animate-pulse rounded-xl" />
                ))}
              </div>
            </div>
          ) : featuredGuides.length > 0 ? (
            <div className="space-y-6 mb-10">
              {/* Hero — magazine-style featured guide */}
              <Link href={`/style-hub/${featuredGuides[0].slug}`}>
                <article className="group grid grid-cols-1 md:grid-cols-5 gap-0 rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer">
                  <div className="md:col-span-3 relative aspect-[16/10] md:aspect-auto md:min-h-[360px] overflow-hidden">
                    {featuredGuides[0].featuredImage ? (
                      <img
                        src={featuredGuides[0].featuredImage}
                        alt={featuredGuides[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-cream/60 via-soft-blush/15 to-warm-white" />
                    )}
                    <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-sm">
                      <BookOpen className="w-3.5 h-3.5 text-soft-blush-ink" />
                      <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-soft-blush-ink">
                        Featured guide
                      </span>
                    </div>
                    {featuredGuides[0].readingTime && (
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium rounded-full px-2.5 py-1 shadow-sm">
                        <Clock className="w-3 h-3" />
                        {featuredGuides[0].readingTime} min read
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2 p-6 md:p-10 flex flex-col justify-center bg-cream/40">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-soft-blush-ink mb-3">
                      {featuredGuides[0].type === "ask-maria" ? "Ask Maria" : "Guide"}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-serif font-bold text-charcoal dark:text-white mb-3 group-hover:text-soft-blush transition-colors leading-tight">
                      {featuredGuides[0].title}
                    </h3>
                    <p className="text-base text-gray-600 dark:text-gray-300 mb-5 line-clamp-3">
                      {featuredGuides[0].description}
                    </p>
                    <div className="inline-flex items-center text-soft-blush text-sm font-medium group-hover:gap-2 transition-all">
                      Read the guide <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </article>
              </Link>

              {/* Secondary cards — the rest of the featured set */}
              {featuredGuides.length > 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {featuredGuides.slice(1).map((item) => (
                    <ContentCard key={item.slug} item={item} />
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <div className="text-center">
            <Button asChild size="lg">
              <Link href="/style-hub">
                Browse all guides
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
